import {
  DefaultDropdownProps,
  FormFieldsData,
  LoadDropdown,
  Option,
  PAGE_SIZE,
  useFormContext,
  useInputHelper,
} from 'framework/form';
import React, { useEffect, useState } from 'react';
import { get } from 'lodash';
import { ProjectSearchDto, ProjectSuggestionsDto, useNewProjectService } from 'domain/shared/newProject';

const getRelatedProjectDisplayValue = (project?: { name?: string; sowNumber?: StringOrNumber | null } | null) => {
  if (!project?.name) {
    return '';
  }

  return project.sowNumber ? `${project.name} – sow${project.sowNumber}` : project.name;
};

interface RelatedProjectsByAccountDropdownProps extends DefaultDropdownProps {
  accountIdSource: string;
  additionalChangeDeps?: StringOrNumber[];
  onAdditionalChangeCallback?: (project: ProjectSearchDto | undefined) => Promise<FormFieldsData>;
}

const RelatedProjectsByAccountDropdown = ({
  source,
  accountIdSource,
  additionalChangeDeps = [],
  onAdditionalChangeCallback,
  ...props
}: RelatedProjectsByAccountDropdownProps) => {
  const { getById, searchSow } = useNewProjectService();

  const { getRawOnChangeCallback, getValue } = useInputHelper<StringOrNumber | undefined>({ source, ...props });
  const rawOnChangeCallback = getRawOnChangeCallback();
  const value = getValue();

  const [loadedProjects, setLoadedProjects] = useState<ProjectSearchDto[]>([]);

  const { data = {} } = useFormContext();
  const accountId = get(data, accountIdSource);

  const loadSuggestions = async () => {
    const projects = await searchSow({
      filter: {
        accountId: { eq: accountId },
      },
      pageSize: PAGE_SIZE,
    });

    setLoadedProjects(projects.result);
    return projects;
  };

  const getProjectById = async (projectId: StringOrNumber) => {
    const loadedProject = loadedProjects.find(project => project.id === projectId);

    if (loadedProject?.defaultBudgetId) {
      return loadedProject;
    }

    const project = await getById(String(projectId));

    setLoadedProjects(prev => [...prev.filter(item => item.id !== project.id), project]);

    return project;
  };

  const mapResult = (projects: ProjectSuggestionsDto[]): Option<StringOrNumber>[] => {
    return projects.map(project => ({
      value: project.id,
      label: getRelatedProjectDisplayValue(project),
    }));
  };

  const onChangeCallback = async (values: FormFieldsData) => {
    if (!rawOnChangeCallback) {
      return;
    }

    if (onAdditionalChangeCallback) {
      const projectId = get(values, source);
      const projectData = projectId ? await getProjectById(projectId) : undefined;

      const result = await onAdditionalChangeCallback(projectData);
      rawOnChangeCallback({ ...values, ...(result || {}) });
    } else {
      rawOnChangeCallback(values);
    }
  };

  useEffect(() => {
    if (!value || !rawOnChangeCallback || !onAdditionalChangeCallback) {
      return;
    }

    getProjectById(value)
      .then(project => onAdditionalChangeCallback(project))
      .then(result => {
        rawOnChangeCallback({
          [source]: value,
          ...(result || {}),
        });
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [...additionalChangeDeps]);

  return (
    <LoadDropdown
      source={source}
      loadDataMethod={loadSuggestions}
      mapResult={mapResult}
      onChangeCallback={onChangeCallback}
      showLoader
      placeholder="Select project"
      deps={[accountId]}
      {...props}
    />
  );
};

export default RelatedProjectsByAccountDropdown;
